import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faShoppingCart, faUser } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Sheet from "components/sheet/Sheet";
import Callout from "components/callout/Callout";
import Checkbox from "components/form/checkbox/Checkbox";
import { ICart } from "redux/reducers/models/cart";
import { LocaleCurrency } from "helpers/Locale";

import "./combineTabsModal.scss";

interface ICombineTabsModalProps {
  open: boolean;
  targetTab: ICart | null;
  availableTabs: ICart[];
  onCancel: () => void;
  onOk: (sourceTabId: number) => void;
  loading?: boolean;
}

function getTabLabel(tab: ICart): string {
  return tab?.customer?.full_name || tab?.description || "Unnamed tab";
}

function getTabItemCount(tab: ICart): number {
  if (typeof tab?.parent_line_item_count === "number") {
    return tab.parent_line_item_count;
  }

  return tab?.line_items?.filter(lineItem => !lineItem.parent_id).length ?? 0;
}

export default function CombineTabsModal(props: ICombineTabsModalProps) {
  const { open, targetTab, availableTabs, onCancel, onOk, loading } = props;
  const { t } = useTranslation();
  const [selectedTabId, setSelectedTabId] = useState<number | null>(null);

  const otherTabs = useMemo(
    () => availableTabs.filter(tab => tab.id !== targetTab?.id && tab.status !== "deleted"),
    [availableTabs, targetTab?.id],
  );

  const selectedTab = useMemo(
    () => otherTabs.find(tab => tab.id === selectedTabId) ?? null,
    [otherTabs, selectedTabId],
  );

  const showCustomerMismatchWarning = useMemo(() => {
    if (!targetTab || !selectedTab) {
      return false;
    }

    const targetCustomerId = targetTab.customer?.id ?? null;
    const selectedCustomerId = selectedTab.customer?.id ?? null;

    return targetCustomerId !== selectedCustomerId;
  }, [selectedTab, targetTab]);

  useEffect(() => {
    if (open) {
      setSelectedTabId(otherTabs.length === 1 ? otherTabs[0].id : null);
    }
  }, [open, targetTab?.id, otherTabs]);

  function handleCancel() {
    setSelectedTabId(null);
    onCancel();
  }

  function handleOk() {
    if (!selectedTabId) {
      return;
    }

    onOk(selectedTabId);
  }

  function handleTabSelect(tabId: number) {
    setSelectedTabId(tabId);
  }

  const targetTabLabel = targetTab ? getTabLabel(targetTab) : "";

  return (
    <Sheet
      title={t("Combine tab")}
      supportText={
        targetTab
          ? t("Select a tab to merge into {{tabName}}. All items from the selected tab will be moved here.", {
              tabName: targetTabLabel,
            })
          : undefined
      }
      open={open}
      size="small"
      closable
      onCancel={handleCancel}
      onOk={handleOk}
      okText={t("Combine")}
      okDisabled={!selectedTabId || loading}
      cancelText={t("Cancel")}
      backDropCancel={false}
    >
      {otherTabs.length > 0 ? (
        <>
          <p className="combine-tabs-modal_label">{t("Select tab to combine")}</p>

          <div className="combine-tabs-modal_list">
            {otherTabs.map(tab => {
              const tabLabel = getTabLabel(tab);

              return (
                <button
                  key={tab.id}
                  type="button"
                  className="combine-tabs-modal_item"
                  onClick={() => handleTabSelect(tab.id)}
                >
                  <div className="combine-tabs-modal_item_selection">
                    <div className="combine-tabs-modal_item_control">
                      <Checkbox
                        size="small"
                        circle
                        checked={selectedTabId === tab.id}
                        onChange={() => handleTabSelect(tab.id)}
                      />
                    </div>
                    <div className="combine-tabs-modal_item_details">
                      <div className="combine-tabs-modal_item_title">
                        {tab.customer ? (
                          <>
                            <FontAwesomeIcon icon={faUser} className="combine-tabs-modal_item_icon" />
                            <span>{tabLabel}</span>
                          </>
                        ) : (
                          <span>{tabLabel}</span>
                        )}
                      </div>
                      <div className="combine-tabs-modal_item_meta">
                        <FontAwesomeIcon icon={faShoppingCart} className="combine-tabs-modal_item_meta_icon" />
                        <span>
                          {getTabItemCount(tab)} {getTabItemCount(tab) === 1 ? t("item") : t("items")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="combine-tabs-modal_item_price">
                    <LocaleCurrency currency={tab.currency} amount={tab.total_price} />
                  </div>
                </button>
              );
            })}
          </div>

          {showCustomerMismatchWarning ? (
            <div className="combine-tabs-modal_warning">
              <Callout
                type="warning"
                title={t("Different customers")}
                content={t(
                  "The selected tab has a different customer than this tab. Items will still be combined into the current tab.",
                )}
              />
            </div>
          ) : null}
        </>
      ) : (
        <Callout
          type="info"
          title={t("No other tabs available")}
          content={t("Create another saved tab before combining.")}
        />
      )}
    </Sheet>
  );
}
